import type * as Mapbox from "mapbox-gl";

import { type AsyncResult, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { IsoLinesRequestKind } from "@/cache/GeoJSONCache";
import { networkCaches } from "@/cache/GlobalCache";
import { isoBandsLayerConfig } from "@/constants/layerConfigAttributes";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers/LayerBase";
import { generateIsoLinesLayerCacheId } from "@/layers/geojson/LayerUtils";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import { buildIsoBandsFillColorExpression } from "@/layers/geojson/isoBandsFillUtils";
import {
  type IsoBandsLayer,
  buildIsoLinesRequest,
  parseFixedBBoxAreaFromCustomOptions,
  readIsoLayerCustomOptions,
} from "@/layers/geojson/isoLayerShared";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { SENTRY_TRANSACTION_NAMES } from "@/sentry/TransactionConst";
import { captureTransaction } from "@/sentry/helpers";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import type { GeoJSONFeatureCollection, IsoLinesRequest } from "@mm/api.meteomatics.com";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";

export class IsoBandsLayerImpl extends MapboxGeoJSONLayer<IsoBandsLayer> {
  readonly mapboxLineLayerId: string;
  readonly mapboxSymbolLayerId: string;

  constructor(id: number, props: IsoBandsLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone, "fill");

    this.mapboxLineLayerId = `${this.humanReadableId()}-line`;
    this.mapboxSymbolLayerId = `${this.humanReadableId()}-symbol`;

    const map = this.scene.getMapboxMap();
    map.addLayer({
      id: this.mapboxLineLayerId,
      type: "line",
      source: this.mapbox_source,
      layout: {
        visibility: this.props.show ? "visible" : "none",
      },
      paint: {
        "line-opacity": this.props.opacity,
        "line-width": this.props.line_width,
        "line-color": this.props.line_color,
      },
    });
    map.addLayer({
      id: this.mapboxSymbolLayerId,
      type: "symbol",
      source: this.mapbox_source,
      layout: {
        visibility: this.props.show ? "visible" : "none",
        "symbol-placement": "line",
        "text-field": [
          "case",
          ["all", ["has", "lower_isovalue"], ["has", "upper_isovalue"]],
          ["concat", ["to-string", ["get", "lower_isovalue"]], " - ", ["to-string", ["get", "upper_isovalue"]]],
          ["coalesce", ["get", "title"], ["to-string", ["get", "isovalue"]], ""],
        ],
        "text-size": this.props.text_size,
        "icon-ignore-placement": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": this.props.text_color,
        "text-opacity": this.props.opacity,
        "text-halo-color": "hsl(0,0%,100%)",
        "text-halo-width": 2,
      },
    });

    this.updateMapboxLayoutProperties({
      visibility: this.props.show ? "visible" : "none",
    });
    this.updateMapboxPaintProperties({
      "fill-color": this.getFillColor(this.props),
      "fill-opacity": this.props.opacity,
    });
  }

  private getFillColor(layer: Pick<IsoBandsLayer, "custom_options" | "values" | "value_range">) {
    const customOptions = readIsoLayerCustomOptions(layer.custom_options);
    return buildIsoBandsFillColorExpression({
      values: layer.values,
      valueRange: layer.value_range,
      isobandIntervals: customOptions.isoband_intervals,
      baseColor: customOptions.fill_color ?? isoBandsLayerConfig.defaultFillColor,
      fillColors: customOptions.fill_colors,
      fillColorMode: customOptions.fill_color_mode,
      fillReverse: customOptions.fill_reverse,
    });
  }

  private updateMapboxLineLayoutProperties(props?: Mapbox.LineLayout) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof Mapbox.LineLayout, Mapbox.LineLayout[keyof Mapbox.LineLayout]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setLayoutProperty(this.mapboxLineLayerId, entry[0], entry[1]);
    }
  }

  private updateMapboxLinePaintProperties(props?: Mapbox.LinePaint) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof Mapbox.LinePaint, Mapbox.LinePaint[keyof Mapbox.LinePaint]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setPaintProperty(this.mapboxLineLayerId, entry[0], entry[1]);
    }
  }

  private updateMapboxSymbolLayoutProperties(props?: Mapbox.SymbolLayout) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof Mapbox.SymbolLayout, Mapbox.SymbolLayout[keyof Mapbox.SymbolLayout]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setLayoutProperty(this.mapboxSymbolLayerId, entry[0], entry[1]);
    }
  }

  private updateMapboxSymbolPaintProperties(props?: Mapbox.SymbolPaint) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof Mapbox.SymbolPaint, Mapbox.SymbolPaint[keyof Mapbox.SymbolPaint]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setPaintProperty(this.mapboxSymbolLayerId, entry[0], entry[1]);
    }
  }

  removeLayer() {
    const map = this.scene.getMapboxMap();
    map.removeLayer(this.mapboxSymbolLayerId);
    map.removeLayer(this.mapboxLineLayerId);
    super.removeLayer();
  }

  moveZIndex(beforeLayerId: string) {
    const map = this.scene.getMapboxMap();
    super.moveZIndex(beforeLayerId);
    map.moveLayer(this.mapboxLineLayerId, beforeLayerId);
    map.moveLayer(this.mapboxSymbolLayerId, beforeLayerId);
  }

  humanReadableId(): string {
    return safeFmt`metx.isobandslayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  createRequest(dateTimeWithOffset: DateTime): IsoLinesRequest {
    const map = this.scene.getMapboxMap();
    const fixedBBoxArea = parseFixedBBoxAreaFromCustomOptions(this.props.custom_options);
    const area = fixedBBoxArea ?? this.computeBoundingBox(0).area;
    const { width, height } = map.getCanvas();

    return buildIsoLinesRequest({
      path: "get_isobands",
      area,
      parameter: this.props.parameter_unit,
      model: this.props.model,
      ensSelect: this.props.ens_select,
      boundingBoxLimit: fixedBBoxArea ? null : getModelBoundingBox(this.props.model),
      datetime: dateTimeWithOffset,
      height,
      width,
      radiusGaussianFilter: this.props.filter_gauss,
      radiusMedianFilter: this.props.filter_median,
      values: this.props.values,
      valueRange: this.props.value_range,
      customOptions: this.props.custom_options,
      calibrated: this.props.calibrated,
    });
  }

  beforeRender(): void {
    if (this.scene.isAnimation()) {
      this.updateTrigger();
    } else {
      this.debouncedUpdateTrigger();
    }
  }

  updateTrigger(): void {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveIsoLine(request, IsoLinesRequestKind.IsoBands, {
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(result);
    this.updateSource(result, dateTimeWithOffset, undefined, {
      keepLastDataWhilePending: this.scene.isAnimation(),
    });
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const tags = {
      layerKind: `${this.props.kind ? this.props.kind : "undefined"}`,
      parameter: `${this.props.parameter_unit}`,
      requestedDate: `${dateTimeWithOffset}`,
    };

    const result = networkCaches.geojson_cache.retrieveIsoLine(request, IsoLinesRequestKind.IsoBands);

    if (!this.scene.isAnimation()) {
      return asVoidAsyncResult(
        result,
        captureTransaction(result.asynchronous, SENTRY_TRANSACTION_NAMES.ISOLINES_LAYER_LOADING_SPEED, tags),
      );
    }

    return asVoidAsyncResult(result);
  }

  checker(): PropsChecker<IsoBandsLayer, LayerBase<IsoBandsLayer>> {
    type CheckerProps = CheckedProps<IsoBandsLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (!changed) {
        return false;
      }

      this.setLayerProps({ [property]: curr[property] });
      switch (property) {
        case "model":
        case "parameter_unit":
        case "ens_select":
        case "calibrated":
        case "filter_median":
        case "filter_gauss":
        case "values":
          this.updateMapboxPaintProperties({
            "fill-color": this.getFillColor(curr),
          });
          this.beforeRender();
          break;
        case "value_range":
          this.updateMapboxPaintProperties({
            "fill-color": this.getFillColor(curr),
          });
          this.beforeRender();
          break;
        case "show":
          this.updateMapboxLayoutProperties({ visibility: curr.show ? "visible" : "none" });
          this.updateMapboxLineLayoutProperties({ visibility: curr.show ? "visible" : "none" });
          this.updateMapboxSymbolLayoutProperties({ visibility: curr.show ? "visible" : "none" });
          break;
        case "opacity":
          this.updateMapboxPaintProperties({
            "fill-opacity": curr[property],
          });
          this.updateMapboxLinePaintProperties({
            "line-opacity": curr[property],
          });
          this.updateMapboxSymbolPaintProperties({
            "text-opacity": curr[property],
          });
          break;
        case "text_size":
          this.updateMapboxSymbolLayoutProperties({
            "text-size": curr[property],
          });
          break;
        case "text_color":
          this.updateMapboxSymbolPaintProperties({
            "text-color": curr[property],
          });
          break;
        case "line_width":
          this.updateMapboxLinePaintProperties({
            "line-width": curr[property],
          });
          break;
        case "line_color":
          this.updateMapboxLinePaintProperties({
            "line-color": curr[property],
          });
          break;
        case "custom_options": {
          const prevOptions = readIsoLayerCustomOptions(prev.custom_options);
          const currOptions = readIsoLayerCustomOptions(curr.custom_options);
          if (
            prevOptions.fill_color !== currOptions.fill_color ||
            prevOptions.fill_colors !== currOptions.fill_colors ||
            prevOptions.fill_color_mode !== currOptions.fill_color_mode ||
            prevOptions.fill_reverse !== currOptions.fill_reverse ||
            prevOptions.isoband_intervals !== currOptions.isoband_intervals
          ) {
            this.updateMapboxPaintProperties({
              "fill-color": this.getFillColor(curr),
            });
          }
          if (
            prevOptions.isoband_intervals !== currOptions.isoband_intervals ||
            prevOptions.fixed_bbox !== currOptions.fixed_bbox ||
            prevOptions.resolution !== currOptions.resolution ||
            (prevOptions as any).api_url_suffix !== (currOptions as any).api_url_suffix
          ) {
            this.beforeRender();
          }
          break;
        }
        case "legend_visible":
          break;
        default: {
          const _exhaustive: never = property;
          return _exhaustive;
        }
      }

      return true;
    };

    return {
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      parameter_unit: checkLayerProp("parameter_unit"),
      calibrated: checkLayerProp("calibrated"),
      show: checkLayerProp("show"),
      text_size: checkLayerProp("text_size"),
      text_color: checkLayerProp("text_color"),
      line_width: checkLayerProp("line_width"),
      line_color: checkLayerProp("line_color"),
      filter_median: checkLayerProp("filter_median"),
      filter_gauss: checkLayerProp("filter_gauss"),
      values: checkLayerProp("values"),
      value_range: checkLayerProp("value_range"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
      legend_visible: checkLayerProp("legend_visible"),
    };
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
      },
    ];
  }

  peekLayerCache(request: IsoLinesRequest): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateIsoLinesLayerCacheId(request, IsoLinesRequestKind.IsoBands);
    return networkCaches.geojson_cache.peekCache(cacheId);
  }
}
